import { Adidas, Jordan, Mcdonalds, NewBalance, Nike } from "@thesvg/react";
import type { ComponentType, ReactNode, SVGProps } from "react";

import type { AriBrandId } from "@/lib/ariBrands";
import { cn } from "@/lib/utils";

const BRAND_ICONS: Record<AriBrandId, ComponentType<SVGProps<SVGSVGElement>>> = {
  nike: Nike,
  adidas: Adidas,
  newbalance: NewBalance,
  jordan: Jordan,
  mcdonalds: Mcdonalds,
};

const ICON_SIZE = {
  xs: "size-3",
  sm: "size-4",
  md: "size-5",
  lg: "size-6",
} as const;

const AVATAR_SIZE = {
  xs: "size-[18px] p-0.5",
  sm: "size-6 p-0.5",
  md: "size-8 p-1",
  lg: "size-10 p-1.5",
} as const;

interface AriBrandIconProps {
  brandId: AriBrandId;
  size?: keyof typeof ICON_SIZE;
  className?: string;
}

export function AriBrandIcon({ brandId, size = "md", className }: AriBrandIconProps) {
  const Icon = BRAND_ICONS[brandId];
  return <Icon aria-hidden className={cn(ICON_SIZE[size], "shrink-0", className)} />;
}

interface AriBrandAvatarProps {
  brandId: AriBrandId;
  size?: keyof typeof AVATAR_SIZE;
  className?: string;
}

/** White tile so full-color brand marks stay legible on any surface */
export function AriBrandAvatar({ brandId, size = "md", className }: AriBrandAvatarProps) {
  const iconSize = size === "lg" ? "lg" : size === "md" ? "md" : "sm";
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded bg-white",
        AVATAR_SIZE[size],
        className,
      )}
    >
      <AriBrandIcon brandId={brandId} size={iconSize} />
    </span>
  );
}

interface AriBrandOrFallbackProps {
  brandId: AriBrandId | null | undefined;
  size?: keyof typeof AVATAR_SIZE;
  fallback: ReactNode;
  className?: string;
}

export function AriBrandOrFallback({ brandId, size = "sm", fallback, className }: AriBrandOrFallbackProps) {
  if (!brandId) return <>{fallback}</>;
  return <AriBrandAvatar brandId={brandId} size={size} className={className} />;
}
